import {match} from 'ts-pattern';

import type {SalesActivityTypeEnum} from '@dms/api/sales';

import type {
  BusinessCaseTimelineEventDirection,
  BusinessCaseTimelineEventKind,
} from '@prompt-bridge/shared/ai-assistant-core';

export type BusinessCaseActivityTimelineMapping = {
  direction: BusinessCaseTimelineEventDirection;
  kind: BusinessCaseTimelineEventKind;
};

export const mapBusinessCaseActivityToTimelineEvent = (
  activityType: SalesActivityTypeEnum
): BusinessCaseActivityTimelineMapping | null =>
  match(activityType)
    .returnType<BusinessCaseActivityTimelineMapping | null>()
    .with('CALL_FROM_CUSTOMER', 'INBOUND_CALL', () => ({direction: 'inbound', kind: 'call'}))
    .with('MESSAGE_FROM_CUSTOMER', () => ({direction: 'inbound', kind: 'message'}))
    // The timeline vocabulary has no rating kind; feedback is an inbound customer message.
    .with('CUSTOMER_RATED_PURCHASE', () => ({direction: 'inbound', kind: 'message'}))
    .with('CUSTOMER_VISIT', () => ({direction: 'inbound', kind: 'visit'}))
    .with('ONLINE_OFFER_EMAIL_OPENED', 'BUYING_ONLINE_OFFER_EMAIL_OPENED', () => ({
      direction: 'inbound',
      kind: 'offerOpened',
    }))
    .with('CALL_TO_CUSTOMER', 'OUTBOUND_CALL', () => ({direction: 'outbound', kind: 'call'}))
    .with('MESSAGE_TO_CUSTOMER', () => ({direction: 'outbound', kind: 'message'}))
    .with(
      'EMAIL_TO_CUSTOMER',
      'THANK_YOU_AFTER_SALE_SENT',
      'CHECK_IN_AFTER_SALE_SENT',
      'AUTOMATIC_CUSTOMER_MESSAGE_SENT',
      'AUTOMATIC_CUSTOMER_MESSAGE_SENT_WITHOUT_DOCUMENT',
      () => ({direction: 'outbound', kind: 'email'})
    )
    .with('SMS_TO_CUSTOMER', () => ({direction: 'outbound', kind: 'sms'}))
    .with('ONLINE_OFFER_SENT', 'BUYING_ONLINE_OFFER_SENT', 'PDF_OFFER_SENT', () => ({
      direction: 'outbound',
      kind: 'offerSent',
    }))
    .with('CHAT_WITH_CUSTOMER', () => ({direction: 'internal', kind: 'message'}))
    .with('TEST_DRIVE_PLANNED', () => ({direction: 'internal', kind: 'testDrivePlanned'}))
    .with('TEST_DRIVE_CANCELLED', 'TEST_DRIVE_DELETED', () => ({
      direction: 'internal',
      kind: 'testDriveCancelled',
    }))
    .with('TEST_DRIVE_COMPLETED', () => ({direction: 'internal', kind: 'testDriveCompleted'}))
    .with('TASK_CREATED', () => ({direction: 'internal', kind: 'taskCreated'}))
    .with('TASK_FINISHED', () => ({direction: 'internal', kind: 'taskFinished'}))
    .with('DEPOSIT_PAYMENT_ISSUED', () => ({direction: 'internal', kind: 'depositIssued'}))
    .with('BALANCE_PAYMENT_ISSUED', 'PURCHASE_PAYMENT_ISSUED', () => ({
      direction: 'internal',
      kind: 'invoiceIssued',
    }))
    .with(
      'BUSINESS_CASE_CREATED',
      'BUSINESS_CASE_CLOSED',
      'CONTRACT_CLOSED',
      'BUSINESS_CASE_OPPORTUNITY_LOST',
      'PAYMENT_PAID',
      'ONLINE_OFFER_SHARING_CANCELED',
      'BUYING_ONLINE_OFFER_SHARING_CANCELED',
      'CONTRACT_CANCELED',
      'BUSINESS_CASE_REOPENED',
      'DEPOSIT_PAYMENT_CANCELED',
      'DEPOSIT_PAYMENT_WITHDRAWN',
      'BALANCE_PAYMENT_CANCELED',
      'BALANCE_PAYMENT_WITHDRAWN',
      'PURCHASE_PAYMENT_CANCELED',
      'PURCHASE_PAYMENT_WITHDRAWN',
      'FULL_PAYMENT_CORRECTIVE_DOCUMENT_CREATED',
      'THANK_YOU_AFTER_SALE_SKIPPED_NO_CONTACT',
      'THANK_YOU_AFTER_SALE_SKIPPED_CUSTOMER_REFUSED',
      'CHECK_IN_AFTER_SALE_CANCELLED_REOPENED',
      'CHECK_IN_AFTER_SALE_CANCELLED_UNSUCCESSFUL',
      'CHECK_IN_AFTER_SALE_SKIPPED_NO_CONTACT',
      'CHECK_IN_AFTER_SALE_SKIPPED_CUSTOMER_REFUSED',
      'CHECK_IN_AFTER_SALE_SKIPPED_ALREADY_RATED',
      'CHECK_IN_AFTER_SALE_SKIPPED_DISABLED',
      'CHECK_IN_AFTER_SALE_SKIPPED_ONLINE_OFFER_UNAVAILABLE',
      'CUSTOMER_REFUSED_COMMUNICATION',
      'AUTOMATIC_CUSTOMER_MESSAGE_SKIPPED_NO_CONTACT',
      'AUTOMATIC_CUSTOMER_MESSAGE_SKIPPED_CUSTOMER_REFUSED',
      () => ({direction: 'internal', kind: 'lifecycle'})
    )
    .with(
      'INTEREST_CREATED',
      'INTEREST_CLOSED',
      'INTEREST_OPENED',
      'INTEREST_OPENED_ON_CUSTOMER_CONTACT',
      'INTEREST_REOPENED',
      'INTEREST_OPPORTUNITY_LOST',
      () => null
    )
    .exhaustive();
